import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ApiLogs() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    client.get(`/api-logs?limit=200`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="api-logs-page">
      <div className="mb-8">
        <p className="data-label mb-2">API · LOGS</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">API Logs</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every outbound call made to the CMS (push webhook + status polls). Non-error entries are kept for 7 days;
          all entries are removed after 30 days. Click a row for full request/response detail.
        </p>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="api-logs-table">
            <thead className="thead">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">Entity</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">HTTP</th>
                <th className="px-4 py-2 text-left">Message</th>
              </tr>
            </thead>
            <tbody>
              {!items &&
                ["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td colSpan={6} className="px-4 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))}
              {items &&
                items.map((l) => (
                  <tr
                    key={l.id}
                    className="row-hover cursor-pointer border-t border-border"
                    data-testid={`api-log-row-${l.id}`}
                    onClick={() => setSelected(l)}
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums text-muted-foreground">
                      {new Date(l.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{l.category}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {l.entity_type}/{l.entity_id?.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2">
                      {l.status === "ok" ? (
                        <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                          ok
                        </span>
                      ) : (
                        <span className="rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-destructive">
                          error
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{l.http_status ?? "—"}</td>
                    <td className="max-w-xs truncate px-4 py-2 text-xs text-muted-foreground">
                      {l.error_message || "—"}
                    </td>
                  </tr>
                ))}
              {items && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-muted-foreground">
                    No API log entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ApiLogDetailDialog log={selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}

function ApiLogDetailDialog({ log, onOpenChange }) {
  return (
    <Dialog open={!!log} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="api-log-detail-dialog">
        <DialogHeader>
          <DialogTitle>API call detail</DialogTitle>
        </DialogHeader>
        {log && (
          <div className="space-y-3 text-xs">
            <Row label="When">{new Date(log.timestamp).toLocaleString()}</Row>
            <Row label="Category">{log.category}</Row>
            <Row label="Entity">{log.entity_type}/{log.entity_id}</Row>
            <Row label="Status">{log.status}</Row>
            <Row label="Method / URL">{log.method} {log.url || "(no call made)"}</Row>
            <Row label="HTTP status">{log.http_status ?? "—"}</Row>
            <Row label="Duration">{log.duration_ms != null ? `${log.duration_ms} ms` : "—"}</Row>
            {log.error_message && (
              <Row label="Error">
                <span className="text-destructive">{log.error_message}</span>
              </Row>
            )}
            <Row label="Request payload">
              <pre className="max-h-40 overflow-auto rounded-md border border-border bg-muted p-2 font-mono text-[11px]">
                {JSON.stringify(log.request_payload ?? null, null, 2)}
              </pre>
            </Row>
            <Row label="Response body">
              <pre className="max-h-40 overflow-auto rounded-md border border-border bg-muted p-2 font-mono text-[11px]">
                {JSON.stringify(log.response_body ?? null, null, 2)}
              </pre>
            </Row>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <p className="data-label mb-1">{label}</p>
      <div className="font-mono">{children}</div>
    </div>
  );
}
